import PhotoSwipeLightbox from 'photoswipe/lightbox';
import PhotoSwipeDynamicCaption from 'photoswipe-dynamic-caption-plugin';
import { getFollowingShiftImagesGallery, getGalleryItems } from './AdjacentGallery';

const mobileMediaQuery = '(max-width: 767px)';
const gallerySelector = '.js-mobile-pswp-gallery';
const itemSelector = '.js-mobile-pswp-item.is-always-visible';

class MobilePhotoSwipe {
    constructor() {
        this.galleries = new Map();
        this.mediaQuery = window.matchMedia(mobileMediaQuery);
        this.syncGalleries = this.syncGalleries.bind(this);
    }

    init() {
        this.syncGalleries(this.mediaQuery);
        this.registerMediaQueryListener();
    }

    registerMediaQueryListener() {
        if (typeof this.mediaQuery.addEventListener === 'function') {
            this.mediaQuery.addEventListener('change', this.syncGalleries);
        } else {
            this.mediaQuery.addListener(this.syncGalleries);
        }
    }

    syncGalleries(event) {
        if (event.matches) {
            this.initGalleries();
        } else {
            this.destroyGalleries();
        }
    }

    initGalleries() {
        document.querySelectorAll(gallerySelector).forEach((gallery) => {
            if (this.galleries.has(gallery)) {
                return;
            }

            const followingShiftImagesGallery = gallery.matches('.content-layout__images')
                ? getFollowingShiftImagesGallery(gallery)
                : null;

            // Adjacent sections share one item list without changing their rendered DOM.
            if (followingShiftImagesGallery) {
                const galleryItems = [...getGalleryItems(gallery), ...getGalleryItems(followingShiftImagesGallery)];
                const galleryRoot = gallery.closest('section')?.parentElement;

                if (galleryRoot) {
                    const lightbox = this.createLightbox(galleryRoot, galleryItems, true);

                    this.galleries.set(gallery, lightbox);
                    this.galleries.set(followingShiftImagesGallery, lightbox);
                    return;
                }
            }

            this.galleries.set(
                gallery,
                this.createLightbox(gallery, itemSelector, gallery.dataset.pswpDynamicCaption === 'true')
            );
        });
    }

    destroyGalleries() {
        new Set(this.galleries.values()).forEach((lightbox) => lightbox.destroy());
        this.galleries.clear();
    }

    createLightbox(gallery, children, hasDynamicCaption) {
        const lightbox = new PhotoSwipeLightbox({
            gallery,
            children,
            pswpModule: () => import('photoswipe'),
        });

        this.setupVideoSlides(lightbox);
        this.setupDynamicCaption(hasDynamicCaption, lightbox);

        lightbox.init();

        return lightbox;
    }

    setupDynamicCaption(hasDynamicCaption, lightbox) {
        if (!hasDynamicCaption) {
            return;
        }

        new PhotoSwipeDynamicCaption(lightbox, {
            type: 'auto',
        });
    }

    setupVideoSlides(lightbox) {
        lightbox.on('contentLoad', (event) => {
            const { content } = event;

            if (content.type !== 'video') {
                return;
            }

            event.preventDefault();
            content.element = this.createVideoSlide(content);
        });
    }

    createVideoSlide(content) {
        const videoSource = content.data.element?.dataset.pswpVideoSrc;

        if (!videoSource) {
            return null;
        }

        const wrapper = document.createElement('div');
        wrapper.className = 'mobile-pswp-video';

        const video = document.createElement('video');
        video.className = 'mobile-pswp-video__player';
        video.controls = true;
        video.playsInline = true;
        video.preload = 'metadata';

        const source = document.createElement('source');
        source.src = videoSource;
        source.type = this.getVideoType(videoSource);

        video.appendChild(source);
        wrapper.appendChild(video);

        return wrapper;
    }

    getVideoType(src) {
        return src && src.includes('.webm') ? 'video/webm' : 'video/mp4';
    }
}

const initMobilePhotoSwipe = () => new MobilePhotoSwipe().init();

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMobilePhotoSwipe, { once: true });
} else {
    initMobilePhotoSwipe();
}
